import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { RouletteActions } from "../Channels/Common/Actions/RouletteActions";
import { RouletteBetConfiguration } from "../CustomComponents/RouletteBetConfiguration";
import { Bet } from "./Bet";
import { ChipSelector } from "./ChipSelector";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { RouletteGridConfiguration } from "../CustomComponents/RouletteGridConfiguration";
import { GridElement } from "./GridElement";
import { HtmlGridSingleBetsElement } from "../HTMLComponents/Custom/HtmlGridSingleBetsElement";
import { HtmlGridEdgeBetsElement } from "../HTMLComponents/Custom/HtmlGridEdgeBetsElement";
import { BetManager } from "../Managers/BetManager";
import { HtmlRouletteTableElement } from "../HTMLComponents/Custom/HtmlRouletteTableElement";
import { Racetrack } from "./Racetrack";
import { Grid } from "./Grid";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { BetElement } from "./BetElement";
import { RouletteView } from "./Roulette/RouletteView";
import { Tweens } from "../Components/Effects/Tweens";
import { StyleManager } from "../Components/Managers/StyleManager";
import { RouletteBetType } from "../CustomComponents/RouletteBetType";

@engineComponent(ComponentTypes.DisplayObject)
export class GridBets extends ContainerElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static GridBetSwitchStyle: string = "SS_gridBet";

    private static GridPortraitBetSwitchStyle: string = "SS_portraitGridBet";

    private _gridElements: { [id: string]: GridElement };

    private _betElements: { [id: string]: BetElement };

    private _betValues: ContainerElement;

    protected htmlSingleBets: HtmlGridSingleBetsElement;

    protected htmlEdgeBets: HtmlGridEdgeBetsElement;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._betElements = {};
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    public createGridBets(
        canvasElement: Grid,
        gridElements: { [id: string]: GridElement },
        gridBetsValue: ContainerElement,
        createHtmlLayer: boolean = true,
    ): void {
        this._gridElements = gridElements;
        this._betValues = gridBetsValue;

        this.createCanvasBetPlaces();

        if (createHtmlLayer) {
            this.createHTMLLayer(canvasElement);
        }
    }

    private createCanvasBetPlaces(): void {
        let x = 0;
        let y = 0;

        //create single
        x = Math.round(RouletteGridConfiguration.numberWidth * 0.5);
        y = Math.round(RouletteGridConfiguration.numberHeight * 0.5);
        this.generateCanvasBetPlace(RouletteBetConfiguration.Single, "N_", RouletteActions.ActionBetSingle, 0, x, y);

        //create split vertical
        x = Math.round(RouletteGridConfiguration.numberWidth * 0.5);
        y = RouletteGridConfiguration.numberHeight;
        this.generateCanvasBetPlace(
            RouletteBetConfiguration.SplitVertical,
            "N_",
            RouletteActions.ActionBetSplitVertical,
            0,
            x,
            y,
        );

        //create split horizontal
        x = 0;
        y = Math.round(RouletteGridConfiguration.numberHeight * 0.5);
        this.generateCanvasBetPlace(
            RouletteBetConfiguration.SplitHorizontal,
            "N_",
            RouletteActions.ActionBetSplitHorizontal,
            0,
            x,
            y,
        );

        //create street
        x = Math.round(RouletteGridConfiguration.numberWidth * 0.5);
        y = RouletteGridConfiguration.zeroHeight;
        this.generateCanvasBetPlace(RouletteBetConfiguration.Street, "N_", RouletteActions.ActionBetStreet, 2, x, y);

        //create corner
        x = 0;
        y = RouletteGridConfiguration.numberHeight;
        this.generateCanvasBetPlace(RouletteBetConfiguration.Corner, "N_", RouletteActions.ActionBetCorner, 0, x, y);

        //create double street
        x = RouletteGridConfiguration.numberWidth;
        y = RouletteGridConfiguration.zeroHeight;
        this.generateCanvasBetPlace(
            RouletteBetConfiguration.DoubleStreet,
            "N_",
            RouletteActions.ActionBetDoubleStreet,
            2,
            x,
            y,
        );

        //create trio
        x = 0;
        y = RouletteGridConfiguration.numberHeight;
        this.generateCanvasBetPlace(RouletteBetConfiguration.Trio, "N_", RouletteActions.ActionBetTrio, 0, x, y);

        //create first four
        x = RouletteGridConfiguration.zeroWidth;
        y = RouletteGridConfiguration.zeroHeight;
        this.generateCanvasBetPlace(
            RouletteBetConfiguration.FirstFour,
            "N_",
            RouletteActions.ActionBetFirstFour,
            0,
            x,
            y,
        );

        //Basket
        //Not implemented

        //create dozen
        x = Math.round(RouletteGridConfiguration.dozenWidth * 0.5);
        y = Math.round(RouletteGridConfiguration.dozenHeight * 0.5);
        this.generateCanvasBetPlace(RouletteBetConfiguration.Dozen, "D_", RouletteActions.ActionBetDozen, 0, x, y);

        //create column
        x = Math.round(RouletteGridConfiguration.columnWidth * 0.5);
        y = Math.round(RouletteGridConfiguration.columnHeight * 0.5);
        this.generateCanvasBetPlace(RouletteBetConfiguration.Column, "C_", RouletteActions.ActionBetColumn, 0, x, y);

        //create paired
        for (let i = 0; i < 6; i++) {
            let index = i < 3 ? 0 : 1;
            let types = [
                RouletteActions.ActionBetLowOrHigh,
                RouletteActions.ActionBetEvenOrOdd,
                RouletteActions.ActionBetRedOrBlack,
                RouletteActions.ActionBetRedOrBlack,
                RouletteActions.ActionBetEvenOrOdd,
                RouletteActions.ActionBetLowOrHigh,
            ];
            let elem: SwitchElement = this._gridElements[types[i] + index];
            x = Math.round(RouletteGridConfiguration.pairedWidth * 0.5);
            y = Math.round(RouletteGridConfiguration.pairedHeight * 0.5);

            let betPlace = this.createBetElement(types[i] + index);
            betPlace.x = elem.x + x;
            betPlace.y = elem.y + y;

            //move value to upper layer over all bets
            let valueElement = <ContainerElement>betPlace.getChildElementByName("Value");
            if (this._betValues != null && valueElement != null) {
                valueElement.x = elem.x + x;
                valueElement.y = elem.y + y;
                this._betValues.addChild(valueElement);
            }

            this._betElements[types[i] + index] = betPlace;
            this.addChild(betPlace);
        }
    }

    private createHTMLLayer(canvasElement: Grid): void {
        this.htmlSingleBets = new HtmlGridSingleBetsElement(
            "mainTableControls",
            "singleGridBetPlaces",
            "gridBetPlaces single",
            this._betElements,
            canvasElement,
            RouletteGridConfiguration.gridWidth,
            RouletteGridConfiguration.gridHeight,
        );
        this.htmlEdgeBets = new HtmlGridEdgeBetsElement(
            "mainTableControls",
            "edgeGridBetPlaces",
            "gridBetPlaces edge",
            this._betElements,
            canvasElement,
            RouletteGridConfiguration.gridWidth,
            RouletteGridConfiguration.gridHeight,
        );

        HtmlRouletteTableElement.Emitter.on(HtmlGridEdgeBetsElement.MouseOverBet, this.onMouseOver.bind(this));
        HtmlRouletteTableElement.Emitter.on(HtmlGridEdgeBetsElement.MouseOutBet, this.onMouseOut.bind(this));
    }

    //******************************************************************************************************************
    //  MOUSE INTERACTION
    //******************************************************************************************************************

    private onMouseOver(betType: string, selection: string, fromRacetrack: boolean): void {
        this.handleMouseHover(betType, selection, fromRacetrack, GridElement.CaseSelected);
    }

    private onMouseOut(betType: string, selection: string, fromRacetrack: boolean): void {
        this.handleMouseHover(betType, selection, fromRacetrack, GridElement.CaseNormal);
    }

    private handleMouseHover(betType: string, selection: string, fromRacetrack: boolean, caseType: string): void {
        let numbers: Array<number> = [];

        if (fromRacetrack) {
            let actionIds = Racetrack.getBetsByRacetrackId(betType + "_" + selection);

            for (let i: number = 0; i < actionIds.length; i++) {
                let betTypeArray = actionIds[i].split("_");
                let currentNumbers = BetManager.getBetNumbersByBetType(betTypeArray[0], parseInt(betTypeArray[1]));

                numbers = numbers.concat(currentNumbers);
            }
        } else {
            numbers = BetManager.getBetNumbersByBetType(betType, parseInt(selection));
        }

        for (let i: number = 0; i < numbers.length; i++) {
            this._gridElements["N_" + numbers[i]].goToCase(caseType);
        }

        if (this.isSpecialBet(betType)) {
            betType = betType == RouletteBetType.Dozen ? "D" : betType;
            betType = betType == RouletteBetType.Column ? "C" : betType;

            this._gridElements[betType + "_" + selection].goToCase(caseType);
        }
    }

    //******************************************************************************************************************
    //  HANDLE FUNCTIONS
    //******************************************************************************************************************

    public placeBet(roulleteBet: Bet): void {
        let betPlaceId = roulleteBet.getBetPlaceId();
        let gridBet = <BetElement>this._betElements[betPlaceId];

        if (gridBet != null) {
            gridBet.goToCase("EMPTY");
            let chipsContainer = <ContainerElement>gridBet.getChildElementByName("Chips");

            if (chipsContainer != null) {
                this.removeAllChipsFromBetPlace(chipsContainer);

                let chips = roulleteBet.getChips();
                let offsetY = 0;

                for (let key in chips) {
                    let count = chips[key];
                    let betIndex = parseInt(key);

                    for (let i: number = 0; i < count; i++) {
                        let chip = ChipSelector.getChip(betIndex);
                        chip.position.y -= offsetY;

                        chipsContainer.childs[key + "_" + i] = chip;
                        chipsContainer.addChild(chip);

                        offsetY += RouletteGridConfiguration.ChipOffsetY;
                    }
                }

                if (roulleteBet.value > 0) {
                    gridBet.goToCase("NORMAL");
                }
            }
        }
    }

    public resetBets(): void {
        for (let key in this._betElements) {
            let gridBet = this._betElements[key];
            if (gridBet != null) {
                gridBet.goToCase("EMPTY");
                let chipsContainer = <ContainerElement>gridBet.getChildElementByName("Chips");

                if (chipsContainer != null) {
                    this.removeAllChipsFromBetPlace(chipsContainer);
                }
            }
        }
    }

    private removeAllChipsFromBetPlace(betPlace: ContainerElement): void {
        if (betPlace) {
            for (let key in betPlace.childs) {
                let chip = betPlace.childs[key];
                ChipSelector.returnChip(chip);
                betPlace.removeChild(chip);
            }

            betPlace.childs = {};
        }
    }

    //******************************************************************************************************************
    //  PUBLIC HANDLERS
    //******************************************************************************************************************

    public getBetElements(): { [id: string]: BetElement } {
        return this._betElements;
    }

    //******************************************************************************************************************
    //  LAYOUT
    //******************************************************************************************************************

    public layoutChanged(canvasElement: Grid, width: number, height: number): void {
        if (this.htmlSingleBets) {
            this.htmlSingleBets.setupInstanceHtmlElement(canvasElement, width, height);
        }

        if (this.htmlEdgeBets) {
            this.htmlEdgeBets.setupInstanceHtmlElement(canvasElement, width, height);
        }
    }

    //******************************************************************************************************************
    //  HELPER FUNCTIONS
    //******************************************************************************************************************

    private createBetElement(nodeName: string): BetElement {
        let node: Element = document.createElement(nodeName);
        node.setAttribute("Style", GridBets.GridBetSwitchStyle);
        node.setAttribute("Clone", "true");
        node.setAttribute("Case", "EMPTY");

        if (StyleManager.Styles[GridBets.GridPortraitBetSwitchStyle] != null) {
            node.setAttribute("PortraitStyle", GridBets.GridPortraitBetSwitchStyle);
        }

        let elem = new BetElement();
        elem.create(node);

        //add value background to table color elements
        let value = <ContainerElement>elem.getChildElementByName("Value");
        if (value != null) {
            let valueBg = <ShapeElement>value.childs["Highlight"];
            RouletteView.addColorShapeElement(valueBg);
        }

        return elem;
    }

    private generateCanvasBetPlace(
        betTypeArray: Array<number>,
        elementType: string,
        betType: string,
        indexOffset: number,
        offsetX: number,
        offsetY: number,
    ): void {
        for (let i = 0; i < betTypeArray.length; i++) {
            let index = betTypeArray[i];
            let elem: SwitchElement = this._gridElements[elementType + (index + indexOffset)];
            let x = offsetX;
            let y = offsetY;

            //zero position
            if (betType == RouletteActions.ActionBetSingle && i == 0) {
                x = Math.round(RouletteGridConfiguration.zeroWidth * 0.5);
                y = Math.round(RouletteGridConfiguration.zeroHeight * 0.5);
            }

            let betPlace = this.createBetElement(betType + index);
            betPlace.x = elem.x + x;
            betPlace.y = elem.y + y;

            //move value to upper layer over all bets
            let valueElement = <ContainerElement>betPlace.getChildElementByName("Value");
            if (this._betValues != null && valueElement != null) {
                valueElement.x = elem.x + x;
                valueElement.y = elem.y + y;
                this._betValues.addChild(valueElement);
            }

            this._betElements[betType + index] = betPlace;
            this.addChild(betPlace);
        }
    }

    private isSpecialBet(betType: string): boolean {
        return (
            [
                RouletteBetType.LowOrHigh,
                RouletteBetType.EvenOrOdd,
                RouletteBetType.Dozen,
                RouletteBetType.RedOrBlack,
                RouletteBetType.Column,
            ].indexOf(betType) > -1
        );
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/GridBets.ts
 **/